import React, { useEffect, useState, useRef } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import FullCalendar from '@fullcalendar/react';
import Loader from '../components/Loader';
// FullCalendar plugin import order does matter here
import listPlugin from '@fullcalendar/list';
import dayGridPlugin from '@fullcalendar/daygrid';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Calendar as PRCalendar } from 'primereact/calendar';
import { ColorPicker } from 'primereact/colorpicker';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip as PrimeTooltip } from 'primereact/tooltip';
import EventService from '../service/EventsService';
import ReactTooltip from 'react-tooltip';
import { useHistory } from 'react-router-dom';
import { ET_OFFSET, IS_DEV } from '../utilities/constants';
import { getTimeInEtTimezone } from '../utilities/functions';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import handleDatesRender from '../components/CalendarPage/fullcalendar/functions';
import { VideoValidator } from '../components/PlaylistPage/VideoValidator';
import EventHoverTooltip from '../components/CalendarPage/EventHoverTooltip';
import '../components/CalendarPage/calendar-edit-mode.css';
import '../components/CalendarPage/CalendarPage-modern.css';

// FullCalendar's default order ('start,-duration,allDay,title'), except that
// games with the same start time are ordered by event ID (the NHL game ID),
// matching the MBS schedule and the Dashboard "Game N" numbering. Day labels
// have non-numeric IDs and keep falling back to the title.
const compareByEventId = (a, b) => {
  const idA = Number(a.id);
  const idB = Number(b.id);
  if (!Number.isFinite(idA) || !Number.isFinite(idB)) return 0;
  return idA - idB;
};
const CALENDAR_EVENT_ORDER = ['start', '-duration', 'allDay', compareByEventId, 'title'];

export const CalendarPage = props => {
  const [labelDialog, setLabelDialog] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [savingLabel, setSavingLabel] = useState(false);
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [labels, setLabels] = useState([]);
  const [newLabel, setNewLabel] = useState({
    title: '',
    description: '',
    start: null,
    end: null,
    linkedGameId: null,
    linkedGameTitle: '',
    linkedCompetitionId: null,
    advertiserId: null,
    color: '6b7280',
  });
  const [events, setEvents] = useState(null);
  // Expected-feed completeness for games in the next 7 ET days (NHL users only).
  // Loaded separately from the cached events so it is fresh on every visit.
  const [eventCompleteness, setEventCompleteness] = useState({});
  const [videoValidator, setVideoValidator] = useState(false);
  const [eventsDate, setEventsDate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showClubLogos, setShowClubLogos] = useState(() => {
    try {
      const saved = localStorage.getItem('hub-calendar-club-logos');
      if (saved !== null) return saved === 'true';
    } catch (error) {
      // localStorage may be unavailable in some browser/privacy modes.
    }
    // No saved preference yet: default off on narrower windows, where the
    // matchup row has less room to spare, and on elsewhere.
    return window.innerWidth > 1540;
  });
  const [labelAdvertisers, setLabelAdvertisers] = useState([]);
  const [loadingLabelAdvertisers, setLoadingLabelAdvertisers] = useState(false);
  const [labelAdvertiserCompetitionId, setLabelAdvertiserCompetitionId] = useState(null);
  const [resolvingLabelCompetition, setResolvingLabelCompetition] = useState(false);

  const tooltipRef = useRef();
  const toast = useRef(null);
  const calendarRef = useRef(null);
  const calendarShellRef = useRef(null);
  const eventServiceRef = useRef(new EventService());
  const labelAdvertisersCacheRef = useRef(new Map());
  const history = useHistory();
  const canUseAdvertiserLabels = Number(props.user?.club) === 1;

  // PrimeReact adds this class while a blocking dialog is open. When a route
  // change unmounts a playlist dialog before its exit transition finishes,
  // the class can be left on <body> and the Game Schedule becomes unscrollable.
  // Recover only when there is no active dialog, so real Calendar dialogs keep
  // their intended scroll lock.
  useEffect(() => {
    const restorePageScroll = () => {
      const hasOpenDialog = document.querySelector('.p-dialog-mask.p-dialog-visible');

      if (!hasOpenDialog) {
        document.body.classList.remove('p-overflow-hidden');
      }
    };

    restorePageScroll();
    const restoreTimer = window.setTimeout(restorePageScroll, 0);

    return () => window.clearTimeout(restoreTimer);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('hub-calendar-club-logos', String(showClubLogos));
    } catch (error) {
      // localStorage may be unavailable in some browser/privacy modes.
    }
  }, [showClubLogos]);

  // FullCalendar sets the month grid width in pixels from a measurement it
  // takes while rendering, and only re-measures on window resize events. That
  // first measurement can come out ~15px too wide on reload (and again when a
  // scrollbar appears or disappears), leaving the grid wider than its clipped
  // container so the Sunday column is cut off at the right edge (or too narrow,
  // leaving a gap, when a scrollbar goes away). Re-measure whenever the grid
  // does not match its container.
  useEffect(() => {
    const shell = calendarShellRef.current;
    if (!shell || typeof ResizeObserver === 'undefined') return undefined;

    let timer = 0;
    const fitGridToContainer = () => {
      const harness = shell.querySelector('.fc-view-harness');
      const grid = shell.querySelector('.fc-daygrid-body');
      if (!harness || !grid) return;
      // FullCalendar normally leaves the grid 1-2px narrower than the harness.
      const gap = harness.clientWidth - grid.getBoundingClientRect().width;
      if (gap < -2 || gap > 6) {
        calendarRef.current?.getApi?.()?.updateSize();
      }
    };
    const scheduleFit = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(fitGridToContainer, 50);
    };

    const resizeObserver = new ResizeObserver(scheduleFit);
    resizeObserver.observe(shell);
    const mutationObserver = new MutationObserver(scheduleFit);
    mutationObserver.observe(shell, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.clearTimeout(timer);
    };
  }, []);

  // FullCalendar all-day dates must be date-only strings. Using toISOString() here
  // shifts midnight to the previous day for users west of UTC.
  const formatLocalDate = date => {
    const value = new Date(date);
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseCalendarDate = value => {
    if (!value) return null;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date(value);
  };

  const normalizeLabelColor = color => {
    const value = String(color || '6b7280').replace('#', '').slice(0, 6);
    return `#${value || '6b7280'}`;
  };

  const getLabelTextColor = color => {
    const hex = normalizeLabelColor(color).slice(1);
    const red = parseInt(hex.slice(0, 2), 16);
    const green = parseInt(hex.slice(2, 4), 16);
    const blue = parseInt(hex.slice(4, 6), 16);
    const luminance = (red * 299 + green * 587 + blue * 114) / 1000;
    return luminance > 160 ? '#1f2933' : '#ffffff';
  };

  const getAdvertiserLabelCode = advertiser => {
    const primaryCode = String(advertiser?.code || '').trim().toUpperCase();
    const secondaryCode = String(advertiser?.secondary_code || '').trim().toUpperCase();
    const secondaryName = String(advertiser?.secondary_name || '').trim();

    return secondaryCode && secondaryName ? secondaryCode : primaryCode;
  };

  const getAdvertiserSecondaryName = advertiser => {
    const secondaryCode = String(advertiser?.secondary_code || '').trim();
    const secondaryName = String(advertiser?.secondary_name || '').trim();
    return secondaryCode && secondaryName ? secondaryName : '';
  };

  const getAdvertiserById = advertiserId =>
    labelAdvertisers.find(
      advertiser => String(advertiser.id) === String(advertiserId)
    );

  const advertiserMatchesLabel = ({ advertiserId, title, color }) => {
    if (!advertiserId) return false;

    const advertiser = getAdvertiserById(advertiserId);
    if (!advertiser) return false;

    return (
      getAdvertiserLabelCode(advertiser) === String(title || '').trim().toUpperCase()
      && normalizeLabelColor(advertiser.swimlane_color) === normalizeLabelColor(color)
    );
  };

  const renderCalendarLabelAdvertiser = advertiser => {
    if (!advertiser) return null;

    const code = getAdvertiserLabelCode(advertiser);
    const secondaryName = getAdvertiserSecondaryName(advertiser);
    const color = normalizeLabelColor(advertiser.swimlane_color || '#6B7280');

    return (
      <span className="calendarLabelAdvertiserOption">
        <span
          className="calendarLabelAdvertiserOption__code"
          style={{
            backgroundColor: color,
            color: getLabelTextColor(color),
          }}
        >
          {code || '---'}
        </span>
        <span className="calendarLabelAdvertiserOption__text">
          <span className="calendarLabelAdvertiserOption__name">
            {advertiser.name || 'Unnamed advertiser'}
          </span>
          {secondaryName && (
            <>
              <span className="calendarLabelAdvertiserOption__separator">•</span>
              <span className="calendarLabelAdvertiserOption__secondary">
                {secondaryName}
              </span>
            </>
          )}
        </span>
      </span>
    );
  };

  useEffect(() => {
    if (!canUseAdvertiserLabels || !labelDialog) {
      setLabelAdvertiserCompetitionId(null);
      setResolvingLabelCompetition(false);
      return undefined;
    }

    if (!newLabel.linkedGameId && !newLabel.start) {
      setLabelAdvertiserCompetitionId(10);
      setResolvingLabelCompetition(false);
      return undefined;
    }

    let cancelled = false;
    const labelDate = newLabel.start ? formatLocalDate(newLabel.start) : null;

    setResolvingLabelCompetition(true);

    // Resolve the reporting season from the DB for both label types.
    // Event labels use Event.competition_id directly, while day labels use
    // the selected calendar date (with competition 10 as backend fallback).
    // This avoids relying on potentially stale FullCalendar/localStorage data.
    eventServiceRef.current
      .getCalendarLabelCompetitionForDate({
        date: labelDate,
        eventId: newLabel.linkedGameId || null,
      })
      .then(data => {
        if (cancelled) return;

        const competitionId = Number(data?.competitionId || 10);
        const normalizedCompetitionId =
          Number.isInteger(competitionId) && competitionId > 0
            ? competitionId
            : 10;

        if (
          labelAdvertiserCompetitionId
          && Number(labelAdvertiserCompetitionId) !== normalizedCompetitionId
        ) {
          setNewLabel(current => ({
            ...current,
            advertiserId: null,
          }));
        }

        setLabelAdvertiserCompetitionId(normalizedCompetitionId);

        if (
          newLabel.linkedGameId
          && Number(newLabel.linkedCompetitionId) !== normalizedCompetitionId
        ) {
          setNewLabel(current => ({
            ...current,
            linkedCompetitionId: normalizedCompetitionId,
          }));
        }
      })
      .catch(error => {
        console.error('Unable to resolve calendar label competition', error);

        if (!cancelled) {
          if (
            labelAdvertiserCompetitionId
            && Number(labelAdvertiserCompetitionId) !== 10
          ) {
            setNewLabel(current => ({
              ...current,
              advertiserId: null,
            }));
          }

          // Calendar days outside a configured competition use the current
          // reporting season as a safe fallback.
          setLabelAdvertiserCompetitionId(10);
        }
      })
      .finally(() => {
        if (!cancelled) setResolvingLabelCompetition(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    canUseAdvertiserLabels,
    labelDialog,
    newLabel.linkedGameId,
    newLabel.start,
  ]);

  useEffect(() => {
    if (
      !canUseAdvertiserLabels
      || !labelDialog
      || !labelAdvertiserCompetitionId
    ) {
      setLabelAdvertisers([]);
      setLoadingLabelAdvertisers(false);
      return undefined;
    }

    const competitionId = Number(labelAdvertiserCompetitionId);
    if (!Number.isInteger(competitionId) || competitionId <= 0) {
      setLabelAdvertisers([]);
      setLoadingLabelAdvertisers(false);
      return undefined;
    }

    const cachedAdvertisers = labelAdvertisersCacheRef.current.get(competitionId);
    if (cachedAdvertisers) {
      setLabelAdvertisers(cachedAdvertisers);
      setLoadingLabelAdvertisers(false);

      if (
        newLabel.advertiserId
        && !cachedAdvertisers.some(
          advertiser => String(advertiser.id) === String(newLabel.advertiserId)
        )
      ) {
        setNewLabel(current => ({
          ...current,
          advertiserId: null,
        }));
      }

      return undefined;
    }

    let cancelled = false;
    setLoadingLabelAdvertisers(true);

    eventServiceRef.current
      .getWhitelistedAdvertisersV2({ competitionId })
      .then(data => {
        if (cancelled) return;
        const advertisers = Array.isArray(data) ? data : [];
        labelAdvertisersCacheRef.current.set(competitionId, advertisers);
        setLabelAdvertisers(advertisers);

        if (
          newLabel.advertiserId
          && !advertisers.some(
            advertiser => String(advertiser.id) === String(newLabel.advertiserId)
          )
        ) {
          setNewLabel(current => ({
            ...current,
            advertiserId: null,
          }));
        }
      })
      .catch(error => {
        console.error('Unable to load advertisers for calendar label', error);
        if (!cancelled) {
          setLabelAdvertisers([]);
          toast.current?.show({
            severity: 'warn',
            summary: 'Advertiser list unavailable',
            detail: 'You can still enter a custom tricode and color.',
            life: 4000,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingLabelAdvertisers(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    canUseAdvertiserLabels,
    labelDialog,
    labelAdvertiserCompetitionId,
  ]);

  useEffect(() => {
    document.title = 'Hub | Home';
    setBreadcrumb();

    // invalidate cached events if the user logs in under a different club account
    const previousClub = localStorage.getItem('previousUserClub');

    if (!props.user.club || props.user.club != previousClub) {
      // comparing string and int
      console.log('Different club login. Invalidate cached events');
      localStorage.removeItem('cachedEvents');
      localStorage.removeItem('cacheTimestamp');
      localStorage.setItem('previousUserClub', props.user.club || '');
    }

    eventServiceRef.current
      .getCalendarLabels({ clubId: props.user.club })
      .then(data => setLabels(data || []))
      .catch(error => {
        console.error('Unable to load calendar labels', error);
        setLabels([]);
        toast.current?.show({
          severity: 'error',
          summary: 'Unable to load labels',
          detail: error?.message || 'Calendar labels could not be loaded.',
          life: 5000,
        });
      });

    const calendarEventsCacheVersion = '3';
    const previousCalendarEventsCacheVersion = localStorage.getItem('calendarEventsCacheVersion');

    if (previousCalendarEventsCacheVersion !== calendarEventsCacheVersion) {
      localStorage.removeItem('cachedEvents');
      localStorage.removeItem('cacheTimestamp');
      localStorage.setItem('calendarEventsCacheVersion', calendarEventsCacheVersion);
    }

    const cachedEvents = localStorage.getItem('cachedEvents');
    const cacheTimestamp = localStorage.getItem('cacheTimestamp');
    const cacheExpiryTime = 60 * 60 * 1000; // 1 hour
    const sinceCached = Date.now() - cacheTimestamp;

    if (
      !IS_DEV &&
      cachedEvents &&
      cacheTimestamp &&
      sinceCached < cacheExpiryTime
    ) {
      setEvents(JSON.parse(cachedEvents));
      console.log('Using cached events');
    } else {
      console.log('Events fetched from the DB');
      eventServiceRef.current.getCalendarEventsV3(props.user).then(data => {
        const hour = 60 * 60 * 1000;
        const convertedEvents = data.map(event => {
          const d = new Date(event.et_time);
          const etDateTime = d.getTime() + ET_OFFSET * hour;
          return {
            id: event.id,
            outputs: event.outputs,
            readyPlaylistsCount: event.ded_ready_count,
            title: event.name,
            et: event.et_time,
            competitionId: event.competition_id,
            start: etDateTime,
          };
        });

        setEvents(convertedEvents);
        localStorage.setItem('cachedEvents', JSON.stringify(convertedEvents));
        localStorage.setItem('cacheTimestamp', Date.now());
      });
    }
  }, [props.user.club]);

  useEffect(() => {
    setEventCompleteness({});
    if (props.user.club !== 1) return undefined;

    let cancelled = false;
    eventServiceRef.current
      .getCalendarEventCompleteness()
      .then(data => {
        if (!cancelled) setEventCompleteness(data || {});
      })
      .catch(error => {
        // Keep the regular DED badge when completeness is unavailable.
        console.warn('Unable to load event completeness', error);
      });

    return () => {
      cancelled = true;
    };
  }, [props.user.club]);

  const setBreadcrumb = () => {
    const breadcrumbDiv = document.getElementsByClassName('layout-breadcrumb viewname')[0];
    const breadcrumbText = `Game Schedule`;
    breadcrumbDiv.innerHTML = breadcrumbText;
  };
  const closeLabelDialog = () => {
    if (!savingLabel) {
      setLabelDialog(false);
      setEditingLabelId(null);
      setLabelAdvertiserCompetitionId(null);
      setResolvingLabelCompetition(false);
    }
  };

  const labelDialogTitle = editingLabelId
    ? 'Edit label'
    : newLabel.linkedGameId
      ? 'Add label to game'
      : 'Add label to day';

  const labelDialogSubtitle = editingLabelId
    ? 'Update the short code, description or display color'
    : newLabel.linkedGameId
      ? 'Attach a short operational label to this game'
      : 'Add a short operational label to the selected calendar day';

  const labelDialogHeader = (
    <div className="calendarLabelDialogHeader">
      <span className="calendarLabelDialogHeader__icon">
        <i className="pi pi-tag"></i>
      </span>
      <div className="calendarLabelDialogHeader__text">
        <span className="calendarLabelDialogHeader__kicker">
          Calendar label
        </span>
        <strong>{labelDialogTitle}</strong>
        <small>{labelDialogSubtitle}</small>
      </div>
    </div>
  );

  const labelDialogFooter = (
    <div className="calendarLabelDialogFooter">
      <Button
        type="button"
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text calendarLabelDialogFooter__cancel"
        disabled={savingLabel}
        onClick={closeLabelDialog}
      />
      <Button
        type="button"
        label={editingLabelId ? 'Save changes' : 'Add label'}
        icon="pi pi-check"
        className="calendarLabelDialogFooter__primary"
        loading={savingLabel}
        onClick={() => saveCalendarLabel()}
      />
    </div>
  );


  const openEventPage = eventInfo => {
    const id = eventInfo.id;
    if (props.user.club === 1)
      id && history.push({ pathname: `/event/${id}`, state: { metaInfo: eventInfo } });
  };

  const openPlaylistPage = (id, e) => {
    if (!id) return;

    const url = `/playlist/${id}`;

    if (e?.ctrlKey || e?.metaKey) {
      const base = window.location.href.split('#')[0];
      window.open(`${base}#${url}`, '_blank');
    } else {
      history.push({ pathname: url, state: {} });
    }
  };

  function timeFormat(info) {
    let hours = info.date.hour;
    let minutes = info.date.minute;
    const ampm = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    minutes = minutes < 10 ? '0' + minutes : minutes;
    const strTime = hours + ':' + minutes + ampm + ' ET';

    return <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>{strTime}</span>;
  }
  const CalendarEventTitle = props => {
    const isListView = props.eventInfo.view.type === 'listMonth';
    const titleParts = props.eventInfo.event.title.replace(/\s/g, '').split('@');
    const eventTime = getTimeInEtTimezone(props.eventInfo.event._def.extendedProps.et);
    const timeWithoutTimezone = String(eventTime || '').replace(/\s*ET$/i, '');

    let standardName = false; // XXX @ YYY || XXX@YYY

    if (titleParts.length === 2) {
      if (titleParts[0].length === 3 && titleParts[1].length === 3) standardName = true;
    }

    const renderTeamLogo = code =>
      !props.hideClubLogos && (
        <img
          className="calendarTeamLogo"
          src={`assets/clubs/${code}.svg`}
          alt=""
          aria-hidden="true"
          onError={e => {
            e.currentTarget.style.display = 'none';
          }}
        />
      );

    return (
      <span className="calendarEventTitle">
        <span className={`calendarEventName ${standardName ? 'calendarEventName--standard' : ''}`}>
          {standardName ? (
            <span>
              {isListView ? (
                <span>
                  {renderTeamLogo(titleParts[0])}
                  <span className="calendarTeamName calendarTeamName--away">{titleParts[0]}</span>
                  <span className="calendarAtSymbol">&nbsp;&nbsp;@&nbsp;&nbsp;</span>
                  {renderTeamLogo(titleParts[1])}
                  <span className="calendarTeamName calendarTeamName--home" style={{ width: 36 }}>
                    {titleParts[1]}&nbsp;&nbsp;
                  </span>
                </span>
              ) : (
                <span>
                  {renderTeamLogo(titleParts[0])}
                  <span className="calendarTeamName calendarTeamName--away">{titleParts[0]}</span>
                  <span className="calendarAtSymbol">&nbsp;@&nbsp;</span>
                  {renderTeamLogo(titleParts[1])}
                  <span className="calendarTeamName calendarTeamName--home">{titleParts[1]}</span>
                </span>
              )}
            </span>
          ) : (
            props.eventInfo.event.title
          )}
        </span>
        {props.showTime !== false && (
          <span className="calendarEventLocation">
            <span className="calendarEventTimeValue">{timeWithoutTimezone}</span>
            <span className="calendarEventTimezone"> ET</span>
          </span>
        )}
      </span>
    );
  };

  const openLabelDialog = ({ date, game = null }) => {
    const startDate = new Date(date);
    startDate.setHours(0, 0, 0, 0);

    setNewLabel({
      title: '',
      description: '',
      start: startDate,
      end: startDate,
      linkedGameId: game?.id || null,
      linkedGameTitle: game?.title || '',
      linkedCompetitionId: game?.competitionId || null,
      advertiserId: null,
      color: '6b7280',
    });
    setLabelAdvertiserCompetitionId(game?.competitionId || null);
    setLabelDialog(true);
  };

  const addLabelToDay = date => {
    // With FullCalendar's named-time-zone UTC coercion, an all-day cell is
    // represented by a Date whose UTC components contain the selected day.
    // Rebuild it at local midnight so North American browsers do not display
    // the previous date in PrimeReact's Calendar control.
    const calendarDate = new Date(date);
    const localDate = new Date(
      calendarDate.getUTCFullYear(),
      calendarDate.getUTCMonth(),
      calendarDate.getUTCDate()
    );

    openLabelDialog({ date: localDate });
  };

  const addLabelToGame = eventInfo => {
    openLabelDialog({
      date: eventInfo.event.start,
      game: {
        id: String(eventInfo.event.id),
        title: eventInfo.event.title,
        competitionId: eventInfo.event.extendedProps?.competitionId || null,
      },
    });
  };


  const editCalendarLabel = label => {
    setEditingLabelId(label.id);
    setNewLabel({
      title: label.title || '',
      description: label.description || '',
      start: parseCalendarDate(label.start),
      end: parseCalendarDate(label.start),
      linkedGameId: label.linkedGameId || null,
      linkedGameTitle: label.linkedGameTitle || '',
      linkedCompetitionId: label.linkedCompetitionId || null,
      advertiserId: canUseAdvertiserLabels ? label.advertiserId || null : null,
      color: String(label.color || '#6b7280').replace('#', ''),
    });
    setLabelAdvertiserCompetitionId(
      label.linkedGameId ? label.linkedCompetitionId || null : null
    );
    setLabelDialog(true);
  };

  const deleteCalendarLabel = async label => {
    if (!window.confirm(`Delete label ${label.title}?`)) return;

    try {
      await eventServiceRef.current.deleteCalendarLabel({
        id: label.id,
        clubId: props.user.club,
      });

      setLabels(current =>
        current.filter(item => String(item.id) !== String(label.id))
      );

      toast.current.show({
        severity: 'success',
        summary: 'Label deleted',
        detail: `${label.title} was removed.`,
        life: 3000,
      });
    } catch (error) {
      console.error('Unable to delete calendar label', error);
      toast.current.show({
        severity: 'error',
        summary: 'Unable to delete label',
        detail: error?.message || 'The label could not be deleted.',
        life: 5000,
      });
    }
  };

  const formatLabelTooltipDate = value => {
    const date = parseCalendarDate(value);
    if (!date || Number.isNaN(date.getTime())) return '';

    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const LabelTooltip = ({ label, targetId, gameTitle = '' }) => {
    const labelColor = normalizeLabelColor(label.color);
    const labelContext = gameTitle || (label.linkedGameId ? 'Event label' : 'Full day');
    const dateText = formatLabelTooltipDate(label.start);

    return (
      <PrimeTooltip
        target={`#${targetId}`}
        position="bottom"
        showDelay={120}
        hideDelay={80}
        autoHide={false}
        className="calendar-label-popup-tooltip"
      >
        <div className="calendar-label-popup-tooltip__content">
          <div className="calendar-label-popup-tooltip__header">
            <span className="calendar-label-popup-tooltip__header-icon">
              <i className="pi pi-tag" aria-hidden="true" />
            </span>
            <span className="calendar-label-popup-tooltip__header-text">
              <strong>{dateText || 'Calendar label'}</strong>
              <small>{label.linkedGameId ? 'Event label' : 'Day label'}</small>
            </span>
          </div>

          <div className="calendar-label-popup-tooltip__body">
            <div className="calendar-label-popup-tooltip__heading">
              <span
                className="calendar-label-popup-tooltip__code"
                style={{
                  backgroundColor: labelColor,
                  color: getLabelTextColor(labelColor),
                }}
              >
                {label.title}
              </span>
              <strong>{labelContext}</strong>
            </div>

            {label.description && (
              <div className="calendar-label-popup-tooltip__description">
                {label.description}
              </div>
            )}
          </div>

          <div className="calendar-label-popup-tooltip__actions">
            <button
              type="button"
              className="calendar-label-popup-tooltip__action"
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                editCalendarLabel(label);
              }}
            >
              <i className="pi pi-pencil" aria-hidden="true" />
              Edit label
            </button>
            <button
              type="button"
              className="calendar-label-popup-tooltip__action calendar-label-popup-tooltip__action--delete"
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                deleteCalendarLabel(label);
              }}
            >
              <i className="pi pi-trash" aria-hidden="true" />
              Delete
            </button>
          </div>
        </div>
      </PrimeTooltip>
    );
  };

  const saveCalendarLabel = async () => {
    const title = newLabel.title.trim();

    if (!/^[A-Z]{1,4}$/.test(title)) {
      toast.current.show({
        severity: 'warn',
        summary: 'Invalid label',
        detail: 'Use 1-4 uppercase English letters.',
        life: 3500,
      });
      return;
    }

    const description = newLabel.description.trim();

    if (!newLabel.start) return;

    // A day label always belongs to exactly one calendar day.
    const labelDate = formatLocalDate(newLabel.start);

    const payload = {
      id: editingLabelId || undefined,
      title,
      description,
      start: labelDate,
      end: labelDate,
      linkedGameId: newLabel.linkedGameId,
      linkedGameTitle: newLabel.linkedGameTitle,
      advertiserId: canUseAdvertiserLabels ? newLabel.advertiserId || null : null,
      color: normalizeLabelColor(newLabel.color),
      clubId: props.user.club,
    };

    setSavingLabel(true);
    try {
      const savedLabel = editingLabelId
        ? await eventServiceRef.current.updateCalendarLabel(payload)
        : await eventServiceRef.current.createCalendarLabel(payload);

      if (!savedLabel?.id) {
        throw new Error('The backend did not return the saved label.');
      }

      const label = {
        id: savedLabel.id,
        title: savedLabel.title ?? payload.title,
        description: savedLabel.description ?? payload.description,
        start: savedLabel.start ?? payload.start,
        end: savedLabel.end ?? payload.end,
        linkedGameId: savedLabel.linkedGameId ?? payload.linkedGameId,
        linkedGameTitle: savedLabel.linkedGameTitle ?? payload.linkedGameTitle,
        linkedCompetitionId:
          savedLabel.linkedCompetitionId ?? newLabel.linkedCompetitionId ?? null,
        advertiserId: savedLabel.advertiserId ?? payload.advertiserId ?? null,
        color: savedLabel.color ?? payload.color,
      };

      setLabels(current =>
        editingLabelId
          ? current.map(item => (String(item.id) === String(editingLabelId) ? label : item))
          : [...current, label]
      );

      setLabelDialog(false);
      setEditingLabelId(null);
      toast.current.show({
        severity: 'success',
        summary: editingLabelId ? 'Label updated' : 'Label added',
        detail: editingLabelId
          ? `${label.title} was updated.`
          : label.linkedGameId
            ? 'The label was attached to the game.'
            : 'The label was attached to the selected day.',
        life: 3500,
      });
    } catch (error) {
      console.error('Unable to save calendar label', error);
      toast.current.show({
        severity: 'error',
        summary: editingLabelId ? 'Unable to update label' : 'Unable to add label',
        detail: error?.message || 'The label could not be saved.',
        life: 5000,
      });
    } finally {
      setSavingLabel(false);
    }
  };

  const dayLabelEvents = labels
    .filter(label => !label.linkedGameId)
    .map(label => {
      const startDate = parseCalendarDate(label.start);
      const exclusiveEnd = new Date(startDate);
      exclusiveEnd.setDate(exclusiveEnd.getDate() + 1);
      return {
        id: `calendar-label-${label.id}`,
        title: label.title,
        start: formatLocalDate(startDate),
        end: formatLocalDate(exclusiveEnd),
        allDay: true,
        backgroundColor: normalizeLabelColor(label.color),
        borderColor: normalizeLabelColor(label.color),
        textColor: getLabelTextColor(label.color),
        classNames: ['calendarDayLabelEvent'],
        extendedProps: { isCalendarLabel: true, labelId: label.id, calendarLabel: label },
      };
    });


  const renderEventContent = eventInfo => {
    if (eventInfo.event.extendedProps?.isCalendarLabel) {
      const label = eventInfo.event.extendedProps.calendarLabel;
      const tooltipId = `calendar-day-label-${label.id}`;
      const isListView = String(eventInfo.view?.type || '').startsWith('list');
      return (
        <>
          <LabelTooltip label={label} targetId={tooltipId} />
          <span
            id={tooltipId}
            className={`calendarLabelInteractive ${
              isListView ? 'calendarLabelInteractive--list' : ''
            }`}
            style={
              isListView
                ? {
                    backgroundColor: normalizeLabelColor(label.color),
                    color: getLabelTextColor(label.color),
                  }
                : undefined
            }
            tabIndex={0}
            aria-label={
              label.description
                ? `${label.title}: ${label.description}`
                : label.title
            }
          >
            <span className="calendarLabelText">{eventInfo.event.title}</span>
          </span>
        </>
      );
    }

    const readyPlaylistsCount =
      eventInfo.event?._def?.extendedProps?.readyPlaylistsCount || 0;
    if (!eventInfo) return null;

    const outputs = eventInfo.event?._def?.extendedProps?.outputs || [];
	  const hasOutputs = outputs.length > 0;
    const eventId = String(eventInfo.event.id);
    const firstOutputId = outputs?.[0]?.id;
    const isNhlUser = props.user.club === 1;
    const completeness = isNhlUser ? eventCompleteness[eventId] : undefined;
    const isEventComplete =
      completeness && completeness.completed >= completeness.expected;
    const gameLabels = labels.filter(label => String(label.linkedGameId) === eventId);

    const hasGameLabels = gameLabels.length > 0;

    return (
      <div
        className={`calendarEventWithLabels ${
          hasGameLabels ? 'calendarEventWithLabels--hasGameLabel' : ''
        }`}
      >
        <div className="calendarEventContent">
        {hasGameLabels ? (
          <>
            <span
              onClick={(e) => {
                if (hasOutputs && firstOutputId) {
                  openPlaylistPage(firstOutputId, e);
                } 
                else {
                  toast.current.show({
                    severity: 'warn',
                    summary: 'No playlists for this event so far',
                    detail: 'No playlists',
                    life: 4000,
                  });
                }
              }}
              className="calendarEventLink"
              title={
                hasOutputs
                  ? 'Click to open DED playlist • Ctrl+Click to open in new tab'
                  : 'No playlists for this event so far'
              }
            >
              <CalendarEventTitle
                eventInfo={eventInfo}
                hideClubLogos={hasGameLabels || !showClubLogos}
              />
            </span>

            {gameLabels.map(label => {
              const tooltipId = `calendar-game-label-${eventId}-${label.id}`;
              return (
                <React.Fragment key={label.id}>
                  <LabelTooltip
                    label={label}
                    targetId={tooltipId}
                    gameTitle={label.linkedGameTitle || eventInfo.event.title}
                  />
                  <span
                    id={tooltipId}
                    className="calendarGameLabel calendarLabelInteractive"
                    style={{
                      backgroundColor: normalizeLabelColor(label.color),
                      color: getLabelTextColor(label.color),
                    }}
                    tabIndex={0}
                    aria-label={
                      label.description
                        ? `${label.title}: ${label.description}`
                        : label.title
                    }
                  >
                    <span className="calendarGameLabelTitle">{label.title}</span>
                  </span>
                </React.Fragment>
              );
            })}

            {editMode && (
              <button
                type="button"
                className="calendarAddButton calendarGameAddButton"
                title="Add label to this game"
                aria-label={`Add label to ${eventInfo.event.title}`}
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  addLabelToGame(eventInfo);
                }}
              >
                <i className="pi pi-plus" />
              </button>
            )}
          </>
        ) : (
          <>
            {editMode && (
              <button
                type="button"
                className="calendarAddButton calendarGameAddButton"
                title="Add label to this game"
                aria-label={`Add label to ${eventInfo.event.title}`}
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  addLabelToGame(eventInfo);
                }}
              >
                <i className="pi pi-plus" />
              </button>
            )}

            <span
              onClick={(e) => {
                if (hasOutputs && firstOutputId) {
                  openPlaylistPage(firstOutputId, e);
                } 
                else {
                  toast.current.show({
                    severity: 'warn',
                    summary: 'No playlists for this event so far',
                    detail: 'No playlists',
                    life: 4000,
                  });
                }
              }}
              className="calendarEventLink"
              title={
                hasOutputs
                  ? 'Click to open DED playlist • Ctrl+Click to open in new tab'
                  : 'No playlists for this event so far'
              }
            >
              <CalendarEventTitle
                eventInfo={eventInfo}
                hideClubLogos={hasGameLabels || !showClubLogos}
              />
            </span>
          </>
        )}

        <span className="calendarEventActions">
          {isNhlUser ? (
            <EventHoverTooltip
              eventId={eventId}
              events={[...events, ...dayLabelEvents]}
              openEventPage={openEventPage}
              openPlaylistPage={openPlaylistPage}
              hasOutputs={hasOutputs}
              isNhlUser={props.user.club === 1}
              userClubId={props.user.club}
              trigger={
                completeness ? (
                  <span
                    className={`calendarPlaylistReadyBadge calendarPlaylistReadyBadgeProgress ${
                      isEventComplete ? 'calendarPlaylistReadyBadgeComplete' : ''
                    }`}
                    title={
                      isEventComplete
                        ? `All ${completeness.expected} expected DED feeds are completed`
                        : `${completeness.completed} of ${completeness.expected} expected DED feeds completed` +
                          (completeness.incompleteFeeds.length
                            ? ` • Incomplete: ${completeness.incompleteFeeds.join(', ')}`
                            : '')
                    }
                  >
                    <i className="pi pi-check"></i>
                    {completeness.completed}/{completeness.expected}
                  </span>
                ) : (
                  <span
                    className={`calendarPlaylistReadyBadge ${
                      readyPlaylistsCount === 0 ? 'calendarPlaylistReadyBadgeEmpty' : ''
                    }`}
                    title={
                      readyPlaylistsCount > 0
                        ? `${readyPlaylistsCount} DED playlists ready`
                        : 'No DED playlists ready'
                    }
                  >
                    <i className="pi pi-check"></i>
                    {readyPlaylistsCount > 0 ? readyPlaylistsCount : 0}
                  </span>
                )
              }
            />
          ) : (
            <EventHoverTooltip
              eventId={eventId}
              events={[...events, ...dayLabelEvents]}
              openEventPage={openEventPage}
              openPlaylistPage={openPlaylistPage}
              hasOutputs={hasOutputs}
              userClubId={props.user.club}
            />
          )}
        </span>
        </div>
      </div>
    );
  };

  const refreshCalendarSize = () => {
    const calendarApi = calendarRef.current?.getApi?.();
    if (!calendarApi) return;

    // FullCalendar can briefly retain the previous month's width while
    // rebuilding a variable-height month. Recalculate once immediately
    // and once after the new rows have settled.
    window.requestAnimationFrame(() => {
      calendarApi.updateSize();
    });

    window.setTimeout(() => {
      calendarRef.current?.getApi?.()?.updateSize();
    }, 450);
  };

  const handleDates = info => {
    handleDatesRender(info, setVideoValidator, setEventsDate);
    refreshCalendarSize();
  };
  /*
   ***  MAIN  ***
   */

  return (
    <div className="p-grid">
      <div className="p-col-12">
        {videoValidator && (
          <VideoValidator
            isOpen={videoValidator}
            handleClose={() => setVideoValidator(false)}
            event={null}
            date={eventsDate}
          />
        )}
        <div
          ref={calendarShellRef}
          className={`card card-container calendarPage ${
            editMode ? 'calendarEditMode' : ''
          } ${!showClubLogos ? 'calendarClubLogosOff' : ''} ${
            props.user.club !== 1 ? 'calendarClubView' : ''
          }`}
        >
          {showDatePicker && (
            <DatePicker
              fixedHeight
              dateFormat="MM/yyyy"
              inline
              showMonthYearPicker
              onChange={date => {
                calendarRef.current
                  .getApi()
                  .gotoDate(new Date(date.getTime() + 5 * 24 * 60 * 60 * 1000));
                setShowDatePicker(false);
              }}
            />
          )}

          <Toast ref={toast} />
          {events ? (
            <FullCalendar
              ref={calendarRef}
              height={'auto'}
              timeZone="America/New York"
              events={[...events, ...dayLabelEvents]}
              eventOrder={CALENDAR_EVENT_ORDER}
              plugins={[dayGridPlugin, listPlugin]}
              // hide weekday in list view
              listDaySideFormat={false}
              themeSystem={'bootstrap'}
              eventTimeFormat={timeFormat}
              headerToolbar={{
                left: 'twoMonthBack,prev,gotoButton,next,twoMonthFwd',
                center: 'title',
                initialView: 'dayGridMonth',
                right: 'clubLogoToggle,editModeSwitch,dayGridMonth,listMonth',
              }}
              customButtons={{
                clubLogoToggle: {
                  text: (
                    <i
                      className="pi pi-shield calendarClubLogoToggleIcon"
                      aria-hidden="true"
                    />
                  ),
                  hint: showClubLogos
                    ? 'Hide club logos in matchups'
                    : 'Show club logos in matchups',
                  click: () => setShowClubLogos(value => !value),
                },
                editModeSwitch: {
                  text: (
                    <i
                      className="pi pi-tag calendarEditModeButtonIcon"
                      aria-hidden="true"
                    />
                  ),
                  hint: editMode ? 'Turn off edit mode' : 'Turn on edit mode',
                  click: () => setEditMode(value => !value),
                },
                gotoButton: {
                  text: (
                    <i
                      title="Jump to month"
                      className="layout-menuitem-icon pi pi-fw pi-calendar"
                    />
                  ),
                  click: () => {
                    setShowDatePicker(prevShowDatePicker => !prevShowDatePicker);
                  },
                },
                twoMonthBack: {
                  icon: 'chevrons-left',
                  text: 'Two months back',
                  click: () => {
                    const api = calendarRef.current.getApi();
                    api.incrementDate({ months: -2 });
                  },
                },
                twoMonthFwd: {
                  icon: 'chevrons-right',
                  text: 'Two months forward',
                  click: () => {
                    const api = calendarRef.current.getApi();
                    api.incrementDate({ months: 2 });
                  },
                },
              }}
              buttonText={{ month: 'Calendar', list: 'List' }}
              defaultTimedEventDuration={0}
              firstDay={1}
              // editable
              selectable
              fixedWeekCount={false}
              eventContent={renderEventContent}
              dayCellContent={info => (
                <>
                  <span className="fc-daygrid-day-number">{info.dayNumberText}</span>
                  {editMode && (
                    <button
                      type="button"
                      className="calendarAddButton calendarDayAddButton"
                      title="Add label to this day"
                      aria-label={`Add label on ${info.date.toDateString()}`}
                      onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        addLabelToDay(info.date);
                      }}
                    >
                      <i className="pi pi-plus" />
                    </button>
                  )}
                </>
              )}
              eventsSet={refreshCalendarSize}
              datesSet={handleDates}
            />
          ) : (
            <Loader />
          )}
          <Dialog
            visible={labelDialog}
            style={{ width: '500px', maxWidth: '95vw' }}
            header={labelDialogHeader}
            footer={labelDialogFooter}
            modal
            draggable={false}
            blockScroll
            className="calendarLabelDialog"
            maskClassName="calendarLabelDialogMask"
            closable={!savingLabel}
            closeOnEscape={!savingLabel}
            onHide={closeLabelDialog}
          >
            <div className="p-fluid calendarLabelForm">
              {newLabel.linkedGameId && (
                <div className="calendarLinkedGame">
                  <span className="calendarLinkedGame__icon">
                    <i className="pi pi-calendar"></i>
                  </span>
                  <span className="calendarLinkedGame__text">
                    <small>Linked game</small>
                    <strong>{newLabel.linkedGameTitle}</strong>
                  </span>
                </div>
              )}

              {canUseAdvertiserLabels && (
                <div className="p-field calendarLabelFormField">
                  <label htmlFor="calendar-label-advertiser">
                    Match existing advertiser
                  </label>
                  <Dropdown
                    inputId="calendar-label-advertiser"
                    className="calendarLabelControl calendarLabelAdvertiserDropdown"
                    panelClassName="calendarLabelAdvertiserPanel"
                    value={newLabel.advertiserId}
                    options={labelAdvertisers}
                    optionLabel="name"
                    optionValue="id"
                    filter
                    filterBy="name,secondary_name,code,secondary_code"
                    showClear
                    appendTo={document.body}
                    disabled={savingLabel || resolvingLabelCompetition || loadingLabelAdvertisers}
                    placeholder={
                      resolvingLabelCompetition || loadingLabelAdvertisers
                        ? 'Loading advertisers...'
                        : 'Custom label / select advertiser'
                    }
                    itemTemplate={renderCalendarLabelAdvertiser}
                    valueTemplate={advertiser =>
                      advertiser
                        ? renderCalendarLabelAdvertiser(advertiser)
                        : (
                          <span className="calendarLabelAdvertiserPlaceholder">
                            Custom label / select advertiser
                          </span>
                        )
                    }
                    onChange={event => {
                      const advertiserId = event.value || null;

                      if (!advertiserId) {
                        setNewLabel(current => ({
                          ...current,
                          advertiserId: null,
                        }));
                        return;
                      }

                      const advertiser = labelAdvertisers.find(
                        item => String(item.id) === String(advertiserId)
                      );
                      if (!advertiser) return;

                      const advertiserCode = getAdvertiserLabelCode(advertiser);
                      if (!/^[A-Z]{1,4}$/.test(advertiserCode)) {
                        toast.current?.show({
                          severity: 'warn',
                          summary: 'Invalid advertiser tricode',
                          detail: 'The selected advertiser does not have a 1–4 letter tricode.',
                          life: 4000,
                        });
                        return;
                      }

                      const advertiserColor = normalizeLabelColor(
                        advertiser.swimlane_color || '#6B7280'
                      );

                      setNewLabel(current => ({
                        ...current,
                        advertiserId: advertiser.id,
                        title: advertiserCode,
                        color: advertiserColor.replace('#', ''),
                      }));
                    }}
                  />
                  <small>
                    Selecting an advertiser uses its reporting tricode and swimlane color.
                    Change the tricode or color to make the label custom.
                  </small>
                </div>
              )}

              <div className="p-field calendarLabelFormField">
                <label htmlFor="calendar-label-title">Label *</label>
                <InputText
                  id="calendar-label-title"
                  value={newLabel.title}
                  onChange={event => {
                    const value = event.target.value
                      .toUpperCase()
                      .replace(/[^A-Z]/g, '')
                      .slice(0, 4);
                    setNewLabel(current => ({
                      ...current,
                      title: value,
                      advertiserId: advertiserMatchesLabel({
                        advertiserId: current.advertiserId,
                        title: value,
                        color: current.color,
                      })
                        ? current.advertiserId
                        : null,
                    }));
                  }}
                  maxLength={4}
                  required
                  autoFocus
                  disabled={savingLabel}
                  placeholder="For example: HDIA"
                  className="calendarLabelControl calendarLabelCodeInput"
                />
                <small>1-4 uppercase English letters.</small>
              </div>

              <div className="p-field calendarLabelFormField">
                <label htmlFor="calendar-label-description">Description</label>
                <textarea
                  id="calendar-label-description"
                  className="p-inputtext p-component calendarLabelControl calendarLabelDescriptionInput"
                  rows={4}
                  value={newLabel.description}
                  onChange={event =>
                    setNewLabel(current => ({ ...current, description: event.target.value }))
                  }
                  disabled={savingLabel}
                  placeholder="Optional description"
                />
              </div>

              <div className="p-field calendarLabelFormField">
                <label htmlFor="calendar-label-color">Label color</label>
                <div className="calendarLabelColorField">
                  <ColorPicker
                    inputId="calendar-label-color"
                    value={newLabel.color}
                    onChange={event => {
                      const color = event.value || '6b7280';
                      setNewLabel(current => ({
                        ...current,
                        color,
                        advertiserId: advertiserMatchesLabel({
                          advertiserId: current.advertiserId,
                          title: current.title,
                          color,
                        })
                          ? current.advertiserId
                          : null,
                      }));
                    }}
                    disabled={savingLabel}
                  />
                  <span
                    className="calendarLabelColorPreview"
                    style={{
                      backgroundColor: normalizeLabelColor(newLabel.color),
                      color: getLabelTextColor(newLabel.color),
                    }}
                  >
                    {newLabel.title || 'LABEL'}
                  </span>
                  <InputText
                    className="calendarLabelControl calendarLabelHexInput"
                    value={normalizeLabelColor(newLabel.color)}
                    onChange={event => {
                      const value = event.target.value
                        .replace(/[^0-9a-fA-F#]/g, '')
                        .replace('#', '')
                        .slice(0, 6);
                      setNewLabel(current => ({
                        ...current,
                        color: value,
                        advertiserId: advertiserMatchesLabel({
                          advertiserId: current.advertiserId,
                          title: current.title,
                          color: value,
                        })
                          ? current.advertiserId
                          : null,
                      }));
                    }}
                    maxLength={7}
                    disabled={savingLabel}
                    aria-label="Label color hex value"
                  />
                </div>
              </div>

              {!newLabel.linkedGameId && (
                <div className="p-field calendarLabelFormField">
                  <label htmlFor="calendar-label-start">Date *</label>
                  <PRCalendar
                    id="calendar-label-start"
                    className="calendarLabelControl calendarLabelDateControl"
                    value={newLabel.start}
                    onChange={event =>
                      setNewLabel(current => ({
                        ...current,
                        start: event.value,
                        end: event.value,
                      }))
                    }
                    dateFormat="mm/dd/yy"
                    appendTo={document.body}
                    disabled={savingLabel}
                  />
                  <small>A day label is attached only to this date.</small>
                </div>
              )}
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  );
};
